import type { DatabaseType } from '@/types/discovery'

/**
 * Connection field definition for catalog entries
 */
export interface ConnectionField {
  readonly name: string
  readonly label: string
  readonly type: 'text' | 'number' | 'password' | 'toggle'
  readonly placeholder?: string
  readonly defaultValue?: string | number | boolean
  readonly required?: boolean
}

/**
 * Catalog entry representing a pre-configured application/database template
 */
export interface CatalogEntry {
  readonly id: string
  readonly name: string
  readonly displayName: string
  readonly description: string
  readonly category: CatalogCategory
  readonly brandColor: string
  readonly connectionFields: readonly ConnectionField[]
  /** Maps to Discovery DatabaseType enum — undefined for SaaS/ERP entries */
  readonly databaseType?: DatabaseType
  /** SaaS/ERP entries are disabled with "Coming Soon" badge */
  readonly comingSoon?: boolean
}

export type CatalogCategory = 'ERP' | 'Analytics' | 'Databases'

export const CATALOG_CATEGORIES: readonly CatalogCategory[] = [
  'Databases',
  'ERP',
  'Analytics',
] as const

const DATABASE_CONNECTION_FIELDS: readonly ConnectionField[] = [
  { name: 'host', label: 'Host', type: 'text', placeholder: 'localhost', required: true },
  { name: 'port', label: 'Port', type: 'number', required: true },
  { name: 'database', label: 'Database Name', type: 'text', placeholder: 'mydb', required: true },
  { name: 'username', label: 'Username', type: 'text', placeholder: 'admin', required: true },
  { name: 'password', label: 'Password', type: 'password', required: true },
  { name: 'ssl', label: 'SSL', type: 'toggle', defaultValue: false },
] as const

export const DISCOVERY_CATALOG: readonly CatalogEntry[] = [
  // Databases — fully functional, map to Discovery DatabaseType
  {
    id: 'postgresql',
    name: 'postgresql',
    displayName: 'PostgreSQL',
    description: 'Open-source relational database',
    category: 'Databases',
    brandColor: '#4169E1',
    databaseType: 'POSTGRESQL',
    connectionFields: [
      ...DATABASE_CONNECTION_FIELDS.filter((f) => f.name !== 'port'),
      { name: 'port', label: 'Port', type: 'number' as const, defaultValue: 5432, required: true },
    ],
  },
  {
    id: 'mssql',
    name: 'mssql',
    displayName: 'Microsoft SQL Server',
    description: 'Enterprise relational database management system',
    category: 'Databases',
    brandColor: '#CC2927',
    databaseType: 'MSSQL',
    connectionFields: [
      ...DATABASE_CONNECTION_FIELDS.filter((f) => f.name !== 'port'),
      { name: 'port', label: 'Port', type: 'number' as const, defaultValue: 1433, required: true },
    ],
  },
  {
    id: 'mysql',
    name: 'mysql',
    displayName: 'MySQL',
    description: 'Open-source relational database',
    category: 'Databases',
    brandColor: '#4479A1',
    databaseType: 'POSTGRESQL', // MySQL uses POSTGRESQL driver internally for now
    connectionFields: [
      ...DATABASE_CONNECTION_FIELDS.filter((f) => f.name !== 'port'),
      { name: 'port', label: 'Port', type: 'number' as const, defaultValue: 3306, required: true },
    ],
  },
  {
    id: 'oracle-db',
    name: 'oracle-db',
    displayName: 'Oracle Database',
    description: 'Enterprise multi-model database management system',
    category: 'Databases',
    brandColor: '#F80000',
    databaseType: 'POSTGRESQL', // Oracle uses POSTGRESQL driver internally for now
    connectionFields: [
      ...DATABASE_CONNECTION_FIELDS.filter((f) => f.name !== 'port'),
      { name: 'port', label: 'Port', type: 'number' as const, defaultValue: 1521, required: true },
    ],
  },
  {
    id: 'snowflake',
    name: 'snowflake',
    displayName: 'Snowflake',
    description: 'Cloud data warehouse and analytics platform',
    category: 'Databases',
    brandColor: '#29B5E8',
    databaseType: 'SNOWFLAKE',
    connectionFields: [
      { name: 'host', label: 'Account URL', type: 'text', placeholder: 'account.snowflakecomputing.com', required: true },
      { name: 'port', label: 'Port', type: 'number', defaultValue: 443 },
      { name: 'database', label: 'Database', type: 'text', required: true },
      { name: 'username', label: 'Username', type: 'text', required: true },
      { name: 'password', label: 'Password', type: 'password', required: true },
      { name: 'ssl', label: 'SSL', type: 'toggle', defaultValue: true },
    ],
  },
  {
    id: 'sap-hana',
    name: 'sap-hana',
    displayName: 'SAP HANA',
    description: 'In-memory relational database',
    category: 'Databases',
    brandColor: '#1661BE',
    databaseType: 'POSTGRESQL', // SAP HANA uses POSTGRESQL driver internally for now
    connectionFields: [
      ...DATABASE_CONNECTION_FIELDS.filter((f) => f.name !== 'port'),
      { name: 'port', label: 'Port', type: 'number' as const, defaultValue: 30015, required: true },
    ],
  },
  {
    id: 'bigquery',
    name: 'bigquery',
    displayName: 'Google BigQuery',
    description: 'Serverless data warehouse for analytics',
    category: 'Databases',
    brandColor: '#4285F4',
    databaseType: 'BIGQUERY',
    connectionFields: [
      { name: 'host', label: 'Project ID', type: 'text', placeholder: 'my-gcp-project-id', required: true },
      { name: 'port', label: 'Port', type: 'number', defaultValue: 443 },
      { name: 'database', label: 'Default Dataset', type: 'text', required: true },
      { name: 'username', label: 'Username', type: 'text', required: true },
      { name: 'password', label: 'Service Account JSON Key', type: 'text', required: true },
      { name: 'ssl', label: 'SSL', type: 'toggle', defaultValue: true },
    ],
  },
  {
    id: 'redshift',
    name: 'redshift',
    displayName: 'Amazon Redshift',
    description: 'Cloud data warehouse service',
    category: 'Databases',
    brandColor: '#8C4FFF',
    databaseType: 'REDSHIFT',
    connectionFields: [
      { name: 'host', label: 'Host', type: 'text', placeholder: 'cluster.region.redshift.amazonaws.com', required: true },
      { name: 'port', label: 'Port', type: 'number', defaultValue: 5439, required: true },
      { name: 'database', label: 'Database Name', type: 'text', required: true },
      { name: 'username', label: 'Username', type: 'text', required: true },
      { name: 'password', label: 'Password', type: 'password', required: true },
      { name: 'ssl', label: 'SSL', type: 'toggle', defaultValue: true },
    ],
  },
  {
    id: 'databricks',
    name: 'databricks',
    displayName: 'Databricks',
    description: 'Unified analytics and AI platform',
    category: 'Databases',
    brandColor: '#FF3621',
    databaseType: 'DATABRICKS',
    connectionFields: [
      { name: 'host', label: 'Host', type: 'text', placeholder: 'dbc-xxxxx.cloud.databricks.com', required: true },
      { name: 'port', label: 'Port', type: 'number', defaultValue: 443, required: true },
      { name: 'database', label: 'Catalog', type: 'text', placeholder: 'main', required: true },
      { name: 'username', label: 'Username', type: 'text', required: true },
      { name: 'password', label: 'Personal Access Token', type: 'password', required: true },
      { name: 'ssl', label: 'SSL', type: 'toggle', defaultValue: true },
    ],
  },

  // ERP — Coming Soon
  {
    id: 'sap-s4hana',
    name: 'sap-s4hana',
    displayName: 'SAP S/4HANA',
    description: 'Enterprise resource planning suite with OData APIs',
    category: 'ERP',
    brandColor: '#0FAAFF',
    comingSoon: true,
    connectionFields: [],
  },
  {
    id: 'oracle-fusion',
    name: 'oracle-fusion',
    displayName: 'Oracle Fusion',
    description: 'Cloud ERP, HCM, and CX applications',
    category: 'ERP',
    brandColor: '#F80000',
    comingSoon: true,
    connectionFields: [],
  },
  {
    id: 'zoho-crm',
    name: 'zoho-crm',
    displayName: 'Zoho CRM',
    description: 'Customer relationship management platform',
    category: 'ERP',
    brandColor: '#E42527',
    comingSoon: true,
    connectionFields: [],
  },
  {
    id: 'sap-ariba',
    name: 'sap-ariba',
    displayName: 'SAP Ariba',
    description: 'Procurement and supply chain collaboration',
    category: 'ERP',
    brandColor: '#F0AB00',
    comingSoon: true,
    connectionFields: [],
  },

  // Analytics — Coming Soon
  {
    id: 'sap-analytics-cloud',
    name: 'sap-analytics-cloud',
    displayName: 'SAP Analytics Cloud',
    description: 'Business intelligence, planning, and predictive analytics',
    category: 'Analytics',
    brandColor: '#0070F2',
    comingSoon: true,
    connectionFields: [],
  },
] as const

/**
 * Get catalog entries grouped by category
 */
export function getCatalogByCategory(): ReadonlyMap<CatalogCategory, readonly CatalogEntry[]> {
  const grouped = new Map<CatalogCategory, CatalogEntry[]>()
  for (const category of CATALOG_CATEGORIES) {
    grouped.set(category, [])
  }
  for (const entry of DISCOVERY_CATALOG) {
    grouped.get(entry.category)?.push(entry)
  }
  return grouped
}

/**
 * Find a catalog entry by its ID
 */
export function findCatalogEntry(id: string): CatalogEntry | undefined {
  return DISCOVERY_CATALOG.find((entry) => entry.id === id)
}
